// Centralised storage helpers for the three private buckets.
//
// Path convention:  {parent_record_id}/{random_id}.{ext}
// - Storage RLS joins name's first segment to the metadata table to
//   enforce access — see 20260420000000_storage_buckets.sql.
// - Metadata rows carry the full storage_path; that's what UI reads back
//   to resolve signed URLs.
//
// All buckets are private; reads require `createSignedUrl`.

import { supabase, isSupabaseConfigured } from "./supabase";

export const BUCKETS = {
  reportPhotos: "report-photos",
  incidentAttachments: "incident-attachments",
  enrollmentDocuments: "enrollment-documents",
  familyDocuments: "family-documents",
  childDocuments: "child-documents",
};

const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour

function assertLive() {
  if (!isSupabaseConfigured) {
    throw new Error("Storage not available in demo mode.");
  }
}

function randomId() {
  // short uuid-like; good enough for unique file names
  return (
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2, 10)
  );
}

function extensionOf(filename) {
  const dot = filename.lastIndexOf(".");
  if (dot <= 0) return "";
  return filename.slice(dot).toLowerCase();
}

function buildStoragePath(parentId, filename) {
  const ext = extensionOf(filename);
  const safeExt = /^\.[a-z0-9]{1,8}$/.test(ext) ? ext : "";
  return `${parentId}/${randomId()}${safeExt}`;
}

// ---------------------------------------------------------------------------
// Low-level raw upload/delete against a bucket.
// ---------------------------------------------------------------------------

async function rawUpload({ bucket, path, file }) {
  const { error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || "application/octet-stream",
    });
  if (error) throw error;
}

async function rawRemove({ bucket, path }) {
  const { error } = await supabase.storage.from(bucket).remove([path]);
  if (error) throw error;
}

async function rawSignedUrl({ bucket, path }) {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error) throw error;
  return data.signedUrl;
}

// ---------------------------------------------------------------------------
// Report photos
// ---------------------------------------------------------------------------

export async function uploadReportPhoto({ reportId, file, caption, uploadedBy }) {
  assertLive();
  const path = buildStoragePath(reportId, file.name);
  await rawUpload({ bucket: BUCKETS.reportPhotos, path, file });
  const { data, error } = await supabase
    .from("report_photos")
    .insert({
      daily_report_id: reportId,
      storage_path: path,
      caption: caption || null,
      uploaded_by: uploadedBy || null,
    })
    .select("id, daily_report_id, storage_path, caption, uploaded_by, created_at")
    .single();
  if (error) {
    // best-effort cleanup — we don't want orphaned objects in the bucket
    await rawRemove({ bucket: BUCKETS.reportPhotos, path }).catch(() => {});
    throw error;
  }
  return data;
}

export async function listReportPhotos(reportId) {
  assertLive();
  const { data, error } = await supabase
    .from("report_photos")
    .select("id, daily_report_id, storage_path, caption, uploaded_by, created_at")
    .eq("daily_report_id", reportId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function deleteReportPhoto({ id, storagePath }) {
  assertLive();
  const { error } = await supabase.from("report_photos").delete().eq("id", id);
  if (error) throw error;
  await rawRemove({ bucket: BUCKETS.reportPhotos, path: storagePath }).catch(
    () => {},
  );
}

export function reportPhotoSignedUrl(path) {
  return rawSignedUrl({ bucket: BUCKETS.reportPhotos, path });
}

// ---------------------------------------------------------------------------
// Incident attachments
// ---------------------------------------------------------------------------

export async function uploadIncidentAttachment({
  incidentId,
  file,
  caption,
  uploadedBy,
}) {
  assertLive();
  const path = buildStoragePath(incidentId, file.name);
  await rawUpload({ bucket: BUCKETS.incidentAttachments, path, file });
  const { data, error } = await supabase
    .from("incident_attachments")
    .insert({
      incident_id: incidentId,
      storage_path: path,
      caption: caption || null,
      uploaded_by: uploadedBy || null,
    })
    .select("id, incident_id, storage_path, caption, uploaded_by, created_at")
    .single();
  if (error) {
    await rawRemove({ bucket: BUCKETS.incidentAttachments, path }).catch(() => {});
    throw error;
  }
  return data;
}

export async function listIncidentAttachments(incidentId) {
  assertLive();
  const { data, error } = await supabase
    .from("incident_attachments")
    .select("id, incident_id, storage_path, caption, uploaded_by, created_at")
    .eq("incident_id", incidentId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function deleteIncidentAttachment({ id, storagePath }) {
  assertLive();
  const { error } = await supabase
    .from("incident_attachments")
    .delete()
    .eq("id", id);
  if (error) throw error;
  await rawRemove({
    bucket: BUCKETS.incidentAttachments,
    path: storagePath,
  }).catch(() => {});
}

export function incidentAttachmentSignedUrl(path) {
  return rawSignedUrl({ bucket: BUCKETS.incidentAttachments, path });
}

// ---------------------------------------------------------------------------
// Enrollment documents
// ---------------------------------------------------------------------------

export async function uploadEnrollmentDocument({
  enrollmentId,
  file,
  documentTypeId,
  documentType,
  uploadedBy,
}) {
  assertLive();
  const path = buildStoragePath(enrollmentId, file.name);
  await rawUpload({ bucket: BUCKETS.enrollmentDocuments, path, file });
  const { data, error } = await supabase
    .from("enrollment_documents")
    .insert({
      enrollment_id: enrollmentId,
      storage_path: path,
      document_type_id: documentTypeId || null,
      document_type: documentType || null,
      uploaded_by: uploadedBy || null,
    })
    .select(
      "id, enrollment_id, storage_path, document_type, document_type_id, status, review_note, reviewed_by, reviewed_at, uploaded_by, created_at",
    )
    .single();
  if (error) {
    await rawRemove({ bucket: BUCKETS.enrollmentDocuments, path }).catch(() => {});
    throw error;
  }
  return data;
}

export async function listEnrollmentDocuments(enrollmentId) {
  assertLive();
  const { data, error } = await supabase
    .from("enrollment_documents")
    .select(
      "id, enrollment_id, storage_path, document_type, document_type_id, status, review_note, reviewed_by, reviewed_at, uploaded_by, created_at",
    )
    .eq("enrollment_id", enrollmentId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function deleteEnrollmentDocument({ id, storagePath }) {
  assertLive();
  const { error } = await supabase
    .from("enrollment_documents")
    .delete()
    .eq("id", id);
  if (error) throw error;
  await rawRemove({
    bucket: BUCKETS.enrollmentDocuments,
    path: storagePath,
  }).catch(() => {});
}

export function enrollmentDocumentSignedUrl(path) {
  return rawSignedUrl({ bucket: BUCKETS.enrollmentDocuments, path });
}

// ---------------------------------------------------------------------------
// Family documents (Phase 3 Module 3)
// ---------------------------------------------------------------------------

export async function uploadFamilyDocument({
  familyId,
  organizationId,
  documentTypeId,
  file,
  uploadedBy,
}) {
  assertLive();
  const path = buildStoragePath(familyId, file.name);
  await rawUpload({ bucket: BUCKETS.familyDocuments, path, file });
  const { data, error } = await supabase
    .from("family_documents")
    .insert({
      family_id: familyId,
      organization_id: organizationId,
      document_type_id: documentTypeId || null,
      storage_path: path,
      uploaded_by: uploadedBy || null,
    })
    .select(
      "id, family_id, organization_id, document_type_id, storage_path, uploaded_by, status, reviewed_by, reviewed_at, review_note, signed_at, signed_name, signed_by, signed_ip_address, expires_at, created_at",
    )
    .single();
  if (error) {
    await rawRemove({ bucket: BUCKETS.familyDocuments, path }).catch(() => {});
    throw error;
  }
  return data;
}

export function familyDocumentSignedUrl(path) {
  return rawSignedUrl({ bucket: BUCKETS.familyDocuments, path });
}

// ---------------------------------------------------------------------------
// Child documents (Phase 3 Module 3)
// ---------------------------------------------------------------------------

export async function uploadChildDocument({
  childId,
  organizationId,
  documentTypeId,
  file,
  uploadedBy,
}) {
  assertLive();
  const path = buildStoragePath(childId, file.name);
  await rawUpload({ bucket: BUCKETS.childDocuments, path, file });
  const { data, error } = await supabase
    .from("child_documents")
    .insert({
      child_id: childId,
      organization_id: organizationId,
      document_type_id: documentTypeId || null,
      storage_path: path,
      uploaded_by: uploadedBy || null,
    })
    .select(
      "id, child_id, organization_id, document_type_id, storage_path, uploaded_by, status, reviewed_by, reviewed_at, review_note, signed_at, signed_name, signed_by, signed_ip_address, expires_at, created_at",
    )
    .single();
  if (error) {
    await rawRemove({ bucket: BUCKETS.childDocuments, path }).catch(() => {});
    throw error;
  }
  return data;
}

export function childDocumentSignedUrl(path) {
  return rawSignedUrl({ bucket: BUCKETS.childDocuments, path });
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

const IMAGE_EXT = new Set([".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"]);

export function isImagePath(path) {
  if (!path) return false;
  return IMAGE_EXT.has(extensionOf(path));
}

export function humanFileSize(bytes) {
  if (typeof bytes !== "number" || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let u = 0;
  let n = bytes;
  while (n >= 1024 && u < units.length - 1) {
    n /= 1024;
    u += 1;
  }
  return `${n.toFixed(n < 10 && u > 0 ? 1 : 0)} ${units[u]}`;
}

export function fileBaseName(path) {
  if (!path) return "";
  const trimmed = path.split("/").pop() || path;
  return trimmed;
}
